import { useCallback, useMemo, useState } from "react";
import { DeclineModalInfo, QuestionResponse } from "../types";
import { useTranslations } from "../contexts/LanguageContext";
import { calculateDeclineThresholds } from "../services/calculateDeclineThresholds";

interface DeclineModalHookArgs {
    question?: QuestionResponse | null;
    selectedAnswers: number[];
}


interface DeclineModalHookReturn {
    declineModalInfo: DeclineModalInfo | null;
    setDeclineModalInfo: React.Dispatch<React.SetStateAction<DeclineModalInfo | null>>;
    checkAndTriggerDeclineModal: (actionType?: string | null) => boolean;
    isDeclineAnswer: (answerKey: number) => boolean;
}

/**
 * Custom hook managing all logic related to tracking declines and displaying the modal warning.
 */
export const useDeclineModal = ({ question, selectedAnswers }: DeclineModalHookArgs): DeclineModalHookReturn => {
    const [declineModalInfo, setDeclineModalInfo] = useState<DeclineModalInfo | null>(null);
    const t = useTranslations();

    // --- DECLINE HELPERS ---

    const isDeclineAnswer = useCallback((answerKey: number): boolean => {
        const answerText = question?.Answers?.[answerKey];
        if (!answerText) return false;
        const normalizedText = answerText.toLowerCase().trim();
        return normalizedText.includes("decline") || normalizedText.includes("se negó");
    }, [question?.Answers]);

    const selectedDeclineAnswerKey = selectedAnswers.find(answerKey => isDeclineAnswer(answerKey));

    // --- DECLINE THRESHOLD CALCULATION & MESSAGES ---
    const totalQ = question?.TotalNoQuestions || 18;
    const declineThresholds = useMemo(() => {
        return calculateDeclineThresholds(totalQ);
    }, [totalQ]);

    const orderedThresholds = useMemo(() => {
        return Array.from(declineThresholds).sort((a, b) => a - b);
    }, [declineThresholds]);

    const MODAL_MESSAGES_TEMPLATE = useCallback((t: (key: string, defaultText: string) => string) => [
        { title: t("surveyPage_declineModal_zero_title", "Did you mean to decline this question?"), description: t("surveyPage_declineModal_zero_description", "Your answers help improve youth services, so please answer as many questions as you can."), },
        { title: t("surveyPage_declineModal_first_title", "Your input matters."), description: t("surveyPage_declineModal_first_description", "We understand that some questions may be sensitive, but you must answer some questions in order to qualify for your incentive."), },
        { title: t("surveyPage_declineModal_second_title", "Your feedback is important."), description: t("surveyPage_declineModal_second_description", "It looks like you've declined 5 questions so far, please answer as many questions as possible."), },
        { title: t("surveyPage_declineModal_third_title", "Your voice makes a difference."), description: t("surveyPage_declineModal_third_description", "Please answer as many questions as you can."), },
        { title: t("surveyPage_declineModal_fourth_title", "Please reconsider."), description:t("surveyPage_declineModal_third_description", "Please answer as many questions as you can."), },
    ], []);

    /**
     * Checks if the current submission is a decline and triggers the modal if a threshold is hit.
     * @param actionType The button text ("Next" or "Siguiente").
     * @returns {boolean} True if the modal was triggered and submission should stop, false otherwise.
     */
    const checkAndTriggerDeclineModal = useCallback((actionType?: string | null): boolean => {
        const currentDeclineCount = question?.DeclineCount || 0;
        const templates = MODAL_MESSAGES_TEMPLATE(t);
        let modalInfo: (typeof templates[number]) | null = null;
        
        // Use potential count for threshold check (to catch the very first decline)
        const potentialDeclineCount = currentDeclineCount + 1;
        const thresholdIndex = orderedThresholds.indexOf(potentialDeclineCount);
        
        const lastThreshold = orderedThresholds[orderedThresholds.length - 1] || totalQ;
        const isLateGameTrigger = potentialDeclineCount > lastThreshold;

        const isAttemptingDecline = selectedDeclineAnswerKey && (actionType === "Next" || actionType === "Siguiente");

        if (isAttemptingDecline) {
            if (thresholdIndex !== -1) {
                // Case 1: Exact threshold hit (T1, T2, T3, or T4)
                modalInfo = templates[thresholdIndex];
            } else if (isLateGameTrigger) {
                // Case 2: Past the last specific threshold (T4) -> index 4 for repetition warning
                modalInfo = templates[4];
            }

            if (modalInfo) {
                setDeclineModalInfo({
                    title: modalInfo.title,
                    description: modalInfo.description,
                    yesCaption: t("surveyPage_declineModalYesButton", "Decline this question"),
                    noCaption: t("surveyPage_declineModalNoButton", "Return to question"),
                });
                return true; // Modal triggered, stop submission
            }
        }
        return false; // Submission can proceed
    }, [question?.DeclineCount, orderedThresholds, selectedDeclineAnswerKey, totalQ, t, MODAL_MESSAGES_TEMPLATE]);


    return {
        declineModalInfo,
        setDeclineModalInfo,
        checkAndTriggerDeclineModal,
        isDeclineAnswer,
    };
};